import { ChevronsUpDown, Globe, LogOut, Moon, ShieldCheck, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from 'react-router-dom'
import { Avatar, AvatarFallback } from '~/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import { useTheme } from '~/components/ui/hooks/use-theme'
import { SidebarMenu, SidebarMenuItem, useSidebar } from '~/components/ui/sidebar'
import { useAuth } from '~/helpers/store/auth'
import { useStatsNoticeDialog } from '~/helpers/store/consent'
import useTranslations from '~/helpers/store/locale/translation'
import { cn } from '~/lib/utils'

export function NavUser() {
  const { t, i18n } = useTranslation()
  const { payload: user } = useAuth()
  const { setTheme, theme } = useTheme()
  const navigate = useNavigate()
  const { isMobile, state } = useSidebar()
  const { changeLanguage } = useTranslations()
  const openStatsNotice = useStatsNoticeDialog(s => s.open)

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full">
            <div className={cn(
              'cursor-pointer flex w-full items-center gap-2 rounded-md p-2 text-sm',
              'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              'data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground',
              state === 'collapsed' && 'justify-center',
            )}
            >
              <Avatar className="size-8 rounded-lg">
                <AvatarFallback className="rounded-lg">
                  {`${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}` || 'U'}
                </AvatarFallback>
              </Avatar>
              {state === 'expanded' && (
                <>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{`${user?.firstName} ${user?.lastName}`}</span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </>
              )}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="size-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">
                    {`${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}` || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{`${user?.firstName} ${user?.lastName}`}</span>
                  <span className="truncate text-xs">{user?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="cursor-pointer">
                <Globe className="mr-2 size-4" />
                <span>{t('common.language')}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  onClick={() => changeLanguage('fr')}
                  className={cn(
                    'cursor-pointer',
                    i18n.language === 'fr' && 'bg-accent text-accent-foreground',
                  )}
                >
                  {t('common.french')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => changeLanguage('en')}
                  className={cn(
                    'cursor-pointer',
                    i18n.language === 'en' && 'bg-accent text-accent-foreground',
                  )}
                >
                  {t('common.english')}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className="cursor-pointer"
            >
              {theme === 'light'
                ? (
                    <Moon className="mr-2 size-4" />
                  )
                : (
                    <Sun className="mr-2 size-4" />
                  )}
              <span>{theme === 'light' ? t('theme.dark') : t('theme.light')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openStatsNotice} className="cursor-pointer">
              <ShieldCheck className="mr-2 size-4" />
              <span>{t('notice.stats.manage')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/logout')} className="cursor-pointer">
              <LogOut className="mr-2 size-4" />
              {t('common.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
